import React, { useState, useEffect, useMemo } from 'react';
import { useStorefrontSettings } from '../../hooks/StorefrontSettingsContext';
import Storefront from '../../services/Storefront';
import Box from '@mui/material/Box';
import Loading from '../Loading';
import Button from '@mui/material/Button';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import { GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import Paper from '@mui/material/Paper';
import Notifications from '../../services/Notifications';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { InputLabel, Select, MenuItem } from '@mui/material';
import { useStorefrontAdministrationConfig } from '../../hooks/StorefrontAdministrationConfigContext';
import CheckIcon from '@mui/icons-material/Check';
import TranslatableDataGrid from '../shared/TranslatableDataGrid';
import { ThemeProvider } from '@mui/material/styles';
import StorefrontAdminTheme from '../shared/MUIStyles/StorefrontAdminTheme';
import Typography from '@mui/material/Typography';

import MUIModal from '../shared/MUIModal';
import MUIAlertModal from '../shared/MUIAlertModal';

export const CarrierDetailAdministration = () => {
    const localized = useStorefrontAdministrationConfig().labels;
    const [carrierData, setCarrierData] = useState([]);
    const [freightHandlingCodeData, setFreightHandlingCodeData] = useState([]);
    const [labelData, setLabelData] = useState([]);
    const [currencyCodeData, setCurrencyCodeData] = useState([]);
    const [countryStateCodeData, setCountryStateCodeData] = useState([]);
    const [fetching, setFetching] = useState(true);
    const [carrierId, setCarrierId] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [filteredStateData, setFilteredStateData] = useState([]);
    const [stateCode, setStateCode] = useState('');
    const [currencyCode, setCurrencyCode] = useState('');
    const [freightCode1, setFreightCode1] = useState('');
    const [freightCode2, setFreightCode2] = useState('');
    const [freightCode1Label, setFreightCode1Label] = useState('');
    const [freightCode2Label, setFreightCode2Label] = useState('');
    const [showExtraInformation, setShowExtraInformation] = useState(true);
    const [effectiveDateValue, setEffectiveDateValue] = useState(new Date());
    const [expirationDateValue, setExpirationDateValue] = useState(new Date());
    const [rows, setRows] = useState([]);
    const storeData = useStorefrontSettings();
    const [openUpdate, setOpenUpdate] = React.useState(false);
    const [openDelete, setOpenDelete] = React.useState(false);
    const [openAdd, setOpenAdd] = React.useState(false);
    const [selectedRow, setSelectedRow] = React.useState(null);
    const theme = StorefrontAdminTheme;
    const responseMessages = {
        "GenericFailure": localized.CarrierDetailGenericFailure,
        "DeleteSuccess": localized.CarrierDetailsSuccessfulDelete,
        "UpdateSuccess": localized.CarrierDetailsSuccessfulUpdate,
        "AddSuccess": localized.CarrierDetailsSuccessfulAdd
    };
    var addEffectiveDateValue = new Date();
    var addExpirationDateValue = new Date();

    useEffect(() => {
        getCarrierDetails();
    }, []);

    const customToolbar = () => {
        return (
            <GridToolbarContainer  >
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton
                    slotProps={{
                        tooltip: { title: 'Filter list' }
                    }}
                    variant="outlined"
                    color="update"

                />
                <GridToolbarDensitySelector
                    slotProps={{
                        tooltip: { title: 'Change density' }
                    }}
                    variant="outlined"
                    color="update"
                />

                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    slotProps={{
                        tooltip: { title: 'Export data' }
                    }}
                    variant="outlined"
                    color="update"
                />
                <strong>
                    <Button
                        variant="standard"
                        color="success"
                        style={{ marginLeft: 16 }}
                        onClick={(e) => handleClickOpenAdd(e)}
                    >
                        {localized.StorefrontAdminAdd}
                    </Button>
                </strong>
            </GridToolbarContainer>
        );
    }

    const getCarrierDetails = () => {
        setFetching(true);
        Storefront.getCarrierDetails(storeData.storefrontNumber).then(loaded);
    }

    const updateCarrierDetails = async (form, type) => {
        var useEffectiveDate = type === "add" ? addEffectiveDateValue : effectiveDateValue;
        var useExpirationDate = type === "add" ? addExpirationDateValue : expirationDateValue;
        var request = {
            id: form.id ?? null,
            storefrontNumber: storeData.storefrontNumber,
            carrierId: form.carrierId,
            country: form.countryCode,
            state: form.stateCode,
            currencyCode: form.currencyCode,
            thresholdAmount: form.amount,
            freightCode1: form.freightCode1,
            freightCode2: form.freightCode2,
            label1: form.freightCode1Label,
            label2: form.freightCode2Label,
            showInformation: form.showExtraInformation,
            effectiveDate: useEffectiveDate,
            expirationDate: useExpirationDate,
            operationType: type
        };
        addEffectiveDateValue = new Date();
        addExpirationDateValue = new Date();
        return await Storefront.updateCarrierDetails(request).then((res) => {
            if (res) {
                const notificationType = res.response.endsWith("Success") ? "message" : "error";
                const message = responseMessages[res.response];

                if (message) {
                    Notifications[notificationType](message);
                }
                else {
                    Notifications.error(localized.CarrierDetailsErrorNotification);
                }
                getCarrierDetails();
            }
        });
    }

    const loaded = (data) => {
        if (data && data.carrierDetails && data.freightHandlingCodes) {
            setCarrierData(data.carriers);
            setFreightHandlingCodeData(data.freightHandlingCodes);
            setLabelData(data.labelNames);
            setCurrencyCodeData(data.currencyCodes);
            setCountryStateCodeData(data.countryStateCodes);
            mapRows(data.carrierDetails);
        }
        setFetching(false)
    }

    const mapRows = (carrierDetails) => {
        var rows = [];
        carrierDetails.forEach((carrierDetail) => {
            rows.push({
                id: carrierDetail.id,
                carrierId: carrierDetail.carrierId,
                companyCode: carrierDetail.companyCode,
                country: carrierDetail.country,
                state: carrierDetail.state,
                currencyCode: carrierDetail.currencyCode,
                thresholdAmount: carrierDetail.amount,
                belowThresholdCode: carrierDetail.freightCode1,
                atExceedsThresholdcode: carrierDetail.freightCode2,
                correspondingLabel1: carrierDetail.freightCode1Label,
                correspondingLabel2: carrierDetail.freightCode2Label,
                showInfo: carrierDetail.showExtraInformation,
                effectiveDate: carrierDetail.effectiveDate,
                expirationDate: carrierDetail.expirationDate
            });
        });
        setRows(rows);
    }

    const handleClickOpenUpdate = (e, row) => {
        e.stopPropagation();
        setCurrencyCode(row.currencyCode);
        setFreightCode1(row.atExceedsThresholdcode);
        setFreightCode2(row.belowThresholdCode);
        setFreightCode1Label(row.correspondingLabel1);
        setFreightCode2Label(row.correspondingLabel2);
        setShowExtraInformation(row.showInfo);
        setEffectiveDateValue(row.effectiveDate);
        setExpirationDateValue(row.expirationDate);
        setSelectedRow(row);
        setOpenUpdate(true);
    };

    const handleCloseUpdate = () => {
        setCarrierId("");
        setCurrencyCode("");
        setFreightCode1("");
        setFreightCode2("");
        setFreightCode1Label("");
        setFreightCode2Label("");
        setShowExtraInformation(true);
        setOpenUpdate(false);
    };
    const handleClickOpenDelete = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setOpenDelete(true);
    };

    const handleCloseDelete = () => {
        setOpenDelete(false);
        setEffectiveDateValue(new Date());
        setExpirationDateValue(new Date());
    };

    const handleClickOpenAdd = (e) => {
        e.stopPropagation();
        setOpenAdd(true);
    };

    const handleCloseAdd = () => {
        setOpenAdd(false);
        addEffectiveDateValue = new Date();
        addExpirationDateValue = new Date();
        setCarrierId("");
        setCountryCode("");
        setStateCode("");
        setFilteredStateData([]);
        setCurrencyCode("");
        setFreightCode1("");
        setFreightCode2("");
        setFreightCode1Label("");
        setFreightCode2Label("");
        setShowExtraInformation(true);
    };

    const handleCarrierIdChange = (event) => {
        setCarrierId(event.target.value);
    }

    const handleCurrencyCodeChange = (event) => {
        setCurrencyCode(event.target.value);
    }

    const handleCountryCodeChange = (event) => {
        setCountryCode(event.target.value);
        var filteredStates = countryStateCodeData.filter((country) => country.countryCode === event.target.value);
        setFilteredStateData(filteredStates);
    }

    const handleStateCodeChange = (event) => {
        setStateCode(event.target.value);
    }

    const handleFreightCode1Change = (event) => {
        setFreightCode1(event.target.value);
    }
    const handleFreightCode2Change = (event) => {
        setFreightCode2(event.target.value);
    }

    const handleFreightCode1LabelChange = (event) => {
        setFreightCode1Label(event.target.value);
    }

    const handleFreightCode2LabelChange = (event) => {
        setFreightCode2Label(event.target.value);
    }

    const handleShowExtraInformationChange = (event) => {
        setShowExtraInformation(event.target.value);
    }

    const handleEffectiveDateChangeUpdate = (e) => {
        setEffectiveDateValue(e);
    }

    const handleExpirationDateChangeUpdate = (e) => {
        setExpirationDateValue(e);
    }

    const handleEffectiveDateChangeAdd = (e) => {
        addEffectiveDateValue = e;
    }

    const handleExpirationDateChangeAdd = (e) => {
        addExpirationDateValue = e;
    }

    const renderDeleteButton = (row) => (
        <MUIAlertModal
            open={openDelete}
            onClose={handleCloseDelete}
            title={localized.AreYouSure}
            message={
                <>
                    <h3>{row.carrierId}</h3>
                    <DialogContentText>
                        {localized.AreYouSureDeleteCarrierDetail}
                    </DialogContentText>
                    <DialogContentText>
                        <b>{row.code}</b>
                    </DialogContentText>
                </>
            }
            confirmLabel={localized.StorefrontAdminDelete}
            cancelLabel={localized.StorefrontAdminCancel}
            onConfirm={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("id", row.id);
                const formJson = Object.fromEntries(formData.entries());
                updateCarrierDetails(formJson, "delete");
                handleCloseDelete();
            }}
            confirmButtonProps={{ color: "error" }}
            cancelButtonProps={{ color: "update" }}
        />
    );

    const renderUpdateButton = (row) => (
        <MUIModal
            open={openUpdate}
            onClose={handleCloseUpdate}
            title={localized.UpdateCarrierDetails}
            onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("carrierId", row.carrierId);
                formData.append("id", row.id);
                formData.append("country", row.country);
                formData.append("state", row.state);
                const formJson = Object.fromEntries(formData.entries());
                updateCarrierDetails(formJson, "update");
                handleCloseUpdate();
            }}
            submitLabel={localized.StorefrontAdminUpdate}
            cancelLabel={localized.StorefrontAdminCancel}
            showDefaultButtons={true}
            submitButtonProps={{ color: "update" }}
            cancelButtonProps={{ color: "update" }}
            maxWidth="sm"
            fullWidth
        >
            <DialogContentText sx={{ paddingBottom: "15px" }}>
                <h3>{localized.ValuesEligibleForUpdateBelow}</h3>
            </DialogContentText>

            <InputLabel required id="currencyCode-label">{localized.CurrencyCode}</InputLabel>
            <Select
                required
                fullWidth
                labelId="currencyCode-label"
                id="currencyCode"
                name="currencyCode"
                value={currencyCode}
                label={localized.CurrencyCode}
                onChange={handleCurrencyCodeChange}
                MenuProps={{
                    disablePortal: true,
                    PaperProps: { sx: { zIndex: 1600 } },
                }}
            >
                {currencyCodeData?.map((currencyCode) => (
                    <MenuItem key={currencyCode.currencyCode1} value={currencyCode.currencyCode1}>
                        {currencyCode.currencyCode1 + " - " + currencyCode.description}
                    </MenuItem>
                ))}
            </Select>

            <InputLabel required id="amount-label">{localized.Amount}</InputLabel>
            <TextField
                defaultValue={row.thresholdAmount}
                inputProps={{ maxLength: 15 }}
                required
                placeholder={localized.Amount}
                margin="dense"
                id="amount"
                name="amount"
                type="decimal"
                fullWidth
                variant="outlined"
            />

            {[
                {
                    id: "freightCode1", label: localized.FreightCode1, value: freightCode1, onChange: handleFreightCode1Change, options: freightHandlingCodeData?.map((f) => ({
                        value: f.code,
                        label: f.code,
                    })) },
                {
                    id: "freightCode2", label: localized.FreightCode2, value: freightCode2, onChange: handleFreightCode2Change,
                    options: freightHandlingCodeData?.map((f) => ({
                        value: f.code,
                        label: f.code,
                    })),
                },
                {
                    id: "freightCode1Label", label: localized.AboveCodeLabel, value: freightCode1Label, onChange: handleFreightCode1LabelChange,
                    options: labelData?.map((l) => ({ value: l, label: l })),
                },
                {
                    id: "freightCode2Label", label: localized.BelowCodeLabel, value: freightCode2Label, onChange: handleFreightCode2LabelChange,
                    options: labelData?.map((l) => ({ value: l, label: l })),
                },
                {
                    id: "showExtraInformation", label: localized.ShowExtraInformation, value: showExtraInformation, onChange: handleShowExtraInformationChange,
                    options: [
                        { value: true, label: localized.True },
                        { value: false, label: localized.False },
                    ], },
            ].map(({ id, label, value, onChange, options }) => (
                <React.Fragment key={id}>
                    <InputLabel required id={`${id}-label`}>{label}</InputLabel>
                    <Select
                        required
                        fullWidth
                        labelId={`${id}-label`}
                        id={id}
                        name={id}
                        value={value}
                        label={label}
                        onChange={onChange}
                        MenuProps={{
                            disablePortal: true,
                            PaperProps: { sx: { zIndex: 1600 } },
                        }}
                    >
                        {options?.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                        {/*{(options || freightHandlingCodeData || labelData)?.map((option) => {*/}
                        {/*    const val = typeof option === 'object' ? option.code || option : option;*/}
                        {/*    return (*/}
                        {/*        <MenuItem key={val} value={val}>*/}
                        {/*            {val.toString()}*/}
                        {/*        </MenuItem>*/}
                        {/*    );*/}
                        {/*})}*/}
                    </Select>
                </React.Fragment>
            ))}

            <InputLabel required>{localized.EffectiveDate + ": "}</InputLabel>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    value={new Date(effectiveDateValue)}
                    defaultValue={new Date(row.effectiveDate)}
                    onChange={() => true}
                    onAccept={(x) => handleEffectiveDateChangeUpdate(x, row)}
                    disablePortal
                    slotProps={{
                        textField: {
                            required: true,
                            fullWidth: true,
                            multiline: true,
                            inputProps: { style: { fontSize: 14 } },
                        },
                        popper: {
                            modifiers: [
                                {
                                    name: 'zIndex',
                                    enabled: true,
                                    phase: 'write',
                                    fn: ({ state }) => {
                                        state.styles.popper.zIndex = 1601;
                                    },
                                },
                            ],
                        },
                    }}
                />
            </LocalizationProvider>

            <InputLabel required>{localized.ExpirationDate + ": "}</InputLabel>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    value={new Date(expirationDateValue)}
                    defaultValue={new Date(row.expirationDate)}
                    onChange={() => true}
                    onAccept={(x) => handleExpirationDateChangeUpdate(x, row)}
                    disablePortal
                    slotProps={{
                        textField: {
                            required: true,
                            fullWidth: true,
                            multiline: true,
                            inputProps: { style: { fontSize: 14 } },
                        },
                        popper: {
                            modifiers: [
                                {
                                    name: 'zIndex',
                                    enabled: true,
                                    phase: 'write',
                                    fn: ({ state }) => {
                                        state.styles.popper.zIndex = 1601;
                                    },
                                },
                            ],
                        },
                    }}
                />
            </LocalizationProvider>
        </MUIModal>
    );

    const renderAddButton = () => {
        const uniqueCountryObjects = new Map();
        for (const country of countryStateCodeData) {
            uniqueCountryObjects.set(country.countryCode, {
                countryCode: country.countryCode,
                countryDescription: country.countryDescription,
            });
        }
        const uniqueCountryCodeData = Array.from(uniqueCountryObjects.values());

        return (
            <MUIModal
    open={openAdd}
    onClose={handleCloseAdd}
    title={localized.AddCarrier}
    onSubmit={(event) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);
        const formJson = Object.fromEntries(formData.entries());
        updateCarrierDetails(formJson, "add");
        handleCloseAdd();
    }}
    submitLabel={localized.StorefrontAdminAdd}
    cancelLabel={localized.StorefrontAdminCancel}
    showDefaultButtons={true}
    submitButtonProps={{ color: "update" }}
    cancelButtonProps={{ color: "update" }}
    maxWidth="sm"
    fullWidth
>
    <DialogContentText>{localized.FillRequiredFieldsBelow}</DialogContentText>
    {[
        {
            id: "carrierId",
            label: localized.CarrierId,
            value: carrierId,
            onChange: handleCarrierIdChange,
            options: carrierData?.map((c) => ({
                value: c.carrierId,
                label: `${c.carrierId} - ${c.carrierName}`,
            })),
        },
        {
            id: "currencyCode",
            label: localized.CurrencyCode,
            value: currencyCode,
            onChange: handleCurrencyCodeChange,
            options: currencyCodeData?.map((c) => ({
                value: c.currencyCode1,
                label: `${c.currencyCode1} - ${c.description}`,
            })),
        },
        {
            id: "countryCode",
            label: localized.CountryCode,
            value: countryCode,
            onChange: handleCountryCodeChange,
            options: uniqueCountryCodeData?.map((c) => ({
                value: c.countryCode,
                label: `${c.countryCode} - ${c.countryDescription}`,
            })),
        },
        {
            id: "stateCode",
            label: localized.StateCode,
            value: stateCode,
            onChange: handleStateCodeChange,
            options: filteredStateData?.map((s) => ({
                value: s.stateCode,
                label: `${s.stateCode} - ${s.stateDescription}`,
            })),
        },
        {
            id: "amount",
            label: localized.Amount,
            customComponent: (
                <>
                    <InputLabel required id="amount-label">{localized.Amount}</InputLabel>
                    <TextField
                        inputProps={{ maxLength: 15 }}
                        required
                        margin="dense"
                        id="amount"
                        name="amount"
                        placeholder={localized.Amount}
                        type="decimal"
                        fullWidth
                        variant="outlined"
                    />
                </>
            ),
        },
        {
            id: "freightCode1",
            label: localized.FreightCode1,
            value: freightCode1,
            onChange: handleFreightCode1Change,
            options: freightHandlingCodeData?.map((f) => ({
                value: f.code,
                label: f.code,
            })),
        },
        {
            id: "freightCode2",
            label: localized.FreightCode2,
            value: freightCode2,
            onChange: handleFreightCode2Change,
            options: freightHandlingCodeData?.map((f) => ({
                value: f.code,
                label: f.code,
            })),
        },
        {
            id: "freightCode1Label",
            label: localized.AboveCodeLabel,
            value: freightCode1Label,
            onChange: handleFreightCode1LabelChange,
            options: labelData?.map((l) => ({ value: l, label: l })),
        },
        {
            id: "freightCode2Label",
            label: localized.BelowCodeLabel,
            value: freightCode2Label,
            onChange: handleFreightCode2LabelChange,
            options: labelData?.map((l) => ({ value: l, label: l })),
        },
        {
            id: "showExtraInformation",
            label: localized.ShowExtraInformation,
            value: showExtraInformation,
            onChange: handleShowExtraInformationChange,
            options: [
                { value: true, label: localized.True },
                { value: false, label: localized.False },
            ],
        },
    ].map(({ id, label, value, onChange, options, customComponent }) => (
        <React.Fragment key={id}>
            {customComponent ? (
                customComponent
            ) : (
                <>
                    <InputLabel required id={`${id}-label`}>{label}</InputLabel>
                    <Select
                        required
                        fullWidth
                        labelId={`${id}-label`}
                        id={id}
                        name={id}
                        value={value}
                        label={label}
                        onChange={onChange}
                        MenuProps={{
                            disablePortal: true,
                            PaperProps: { sx: { zIndex: 1600 } },
                        }}
                    >
                        {options?.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </>
            )}
        </React.Fragment>
    ))}
    <InputLabel required>{localized.EffectiveDate + ": "}</InputLabel>
    <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
            value={addEffectiveDateValue}
            defaultValue={new Date()}
            onChange={() => true}
            onAccept={(x) => handleEffectiveDateChangeAdd(x)}
            disablePortal
            slotProps={{
                textField: {
                    required: true,
                    fullWidth: true,
                    multiline: true,
                    inputProps: { style: { fontSize: 14 } },
                },
                popper: {
                    modifiers: [
                        {
                            name: 'zIndex',
                            enabled: true,
                            phase: 'write',
                            fn: ({ state }) => {
                                state.styles.popper.zIndex = 1601;
                            },
                        },
                    ],
                },
            }}
        />
                </LocalizationProvider>

                <InputLabel required>{localized.ExpirationDate + ": "}</InputLabel>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                        value={addExpirationDateValue}
                        defaultValue={new Date()}
                        onChange={() => true}
                        onAccept={(x) => handleExpirationDateChangeAdd(x)}
                        disablePortal
                        slotProps={{
                            textField: {
                                required: true,
                                fullWidth: true,
                                multiline: true,
                                inputProps: { style: { fontSize: 14 } },
                            },
                            popper: {
                                modifiers: [
                                    {
                                        name: 'zIndex',
                                        enabled: true,
                                        phase: 'write',
                                        fn: ({ state }) => {
                                            state.styles.popper.zIndex = 1601;
                                        },
                                    },
                                ],
                            },
                        }}
                    />
                </LocalizationProvider>
</MUIModal>

        
        );
    };

    const columns = [
        {
            field: 'carrierId',
            headerName: localized.CarrierId,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'companyCode',
            headerName: localized.CompanyCode,
            headerAlign: 'center',
            align: 'center',
            flex: 1
        },
        {
            field: 'country',
            headerName: localized.CountryCode,
            headerAlign: 'center',
            align: 'center',
            flex: .5
        },
        {
            field: 'state',
            headerName: localized.StateCode,
            headerAlign: 'center',
            align: 'center',
            flex: .5
        },
        {
            field: 'currencyCode',
            headerName: localized.CurrencyCode,
            headerAlign: 'center',
            align: 'center',
            flex: .5
        },
        {
            field: 'thresholdAmount',
            headerName: localized.Amount,
            headerAlign: 'right',
            align: 'right',
            flex: 1
        },
        {
            field: 'belowThresholdCode',
            headerName: localized.FreightCode1,
            headerAlign: 'center',
            align: 'center',
            flex: .5
        },
        {
            field: 'atExceedsThresholdcode',
            headerName: localized.FreightCode2,
            headerAlign: 'center',
            align: 'center',
            flex: .5
        },
        {
            field: 'correspondingLabel1',
            headerName: localized.AboveCodeLabel,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'correspondingLabel2',
            headerName: localized.BelowCodeLabel,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'showInfo',
            headerName: localized.ShowExtraInformation,
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => {
                return (
                    <strong>
                        {params.value ? <CheckIcon color="success" /> : null}
                    </strong>
                )
            }
        },
        {
            field: 'effectiveDate',
            headerName: localized.EffectiveDate,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'expirationDate',
            headerName: localized.ExpirationDate,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'updateButton',
            headerName: localized.StorefrontAdminUpdate,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="update"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenUpdate(e, params.row)}
                        >
                            {localized.StorefrontAdminUpdate}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true

        },
        {
            field: 'deleteButton',
            headerAlign: 'center',
            align: 'center',
            headerName: localized.StorefrontAdminDelete,
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="error"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenDelete(e, params.row)}
                        >
                            {localized.StorefrontAdminDelete}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true
        }
    ];

    const carrierItems = useMemo(() => {
        return (
            <Paper sx={{ height: 700, width: '100%' }}>
                <TranslatableDataGrid rows={rows} columns={columns} insetToolbar={customToolbar} labels={localized} />
            </Paper>
        );
    }, [rows, localized]);

    return (
        <>
            {
                fetching && <Loading type="brand" />
            }
            {
                !fetching && (localized && Object.keys(localized).length > 0) &&
                <>
                    <ThemeProvider theme={theme}>
                        <Typography variant="h1">{localized.CarrierDetailAdministrationHeader}</Typography>
                        {carrierItems}
                        {openUpdate && renderUpdateButton(selectedRow)}
                        {openDelete && renderDeleteButton(selectedRow)}
                        {openAdd && renderAddButton()}
                    </ThemeProvider>
                </>
            }
        </>
    );
}

export default CarrierDetailAdministration;

